import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Saturo Bundle Selector (SHEKO-style flavor dropdowns).
 *
 * Every bundle has a fixed composition: the customer picks a flavor per
 * slot via dropdowns; single-variant products (Shaker, Messlöffel,
 * E-Book) ride along automatically via data-fixed-items. Every pick
 * becomes a REGULAR cart line tagged with a shared `_bundle_id` +
 * `_bundle_type` line property; the saturo-bundles cart transform
 * merges the complete box into ONE cart line with the configured
 * price. Pricing is NOT computed here — the box price lives on the
 * presentation product and in the bundle-type metaobject.
 *
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLInputElement[]} [slotValues] - Hidden input per slot holding the chosen variant id.
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} addButtonText
 * @property {HTMLElement} errorMessage
 * @extends Component<BundleBuilderRefs>
 */
class BundleBuilderComponent extends Component {
  // No requiredRefs: with no products picked yet (theme editor), the
  // selector body — and every ref in it — is not rendered at all.

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /** Close open dropdowns on outside clicks / Escape. */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeAllSlots();
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape') this.#closeAllSlots();
  };

  #slotElements() {
    return [...this.querySelectorAll('[data-bundle-slot]')];
  }

  #closeAllSlots(except) {
    for (const slot of this.#slotElements()) {
      if (slot === except) continue;
      slot.querySelector('.bundle-selector__options')?.setAttribute('hidden', '');
      slot.querySelector('.bundle-selector__trigger')?.setAttribute('aria-expanded', 'false');
    }
  }

  /** @param {Event} event */
  toggleSlot(event) {
    const slot = /** @type {HTMLElement} */ (event.target).closest('[data-bundle-slot]');
    if (!slot) return;
    const options = slot.querySelector('.bundle-selector__options');
    const trigger = slot.querySelector('.bundle-selector__trigger');
    if (!options || !trigger) return;
    const isOpen = !options.hasAttribute('hidden');
    this.#closeAllSlots(slot);
    options.toggleAttribute('hidden', isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
  }

  /** @param {Event} event */
  chooseOption(event) {
    const option = /** @type {HTMLElement} */ (event.target).closest('.bundle-selector__option');
    const slot = option?.closest('[data-bundle-slot]');
    if (!option || !slot) return;

    const input = slot.querySelector('input[ref="slotValues[]"]');
    if (input instanceof HTMLInputElement) input.value = option.dataset.value || '';

    // Selected state in the list.
    for (const other of slot.querySelectorAll('.bundle-selector__option')) {
      other.setAttribute('aria-selected', String(other === option));
    }

    // Mirror image + label into the trigger.
    const media = slot.querySelector('[data-slot-media]');
    const label = slot.querySelector('[data-slot-label]');
    const optionImage = option.querySelector('img');
    if (media instanceof HTMLElement) {
      media.innerHTML = '';
      if (optionImage) {
        media.appendChild(optionImage.cloneNode(true));
        media.removeAttribute('hidden');
      } else {
        media.setAttribute('hidden', '');
      }
    }
    if (label instanceof HTMLElement) {
      label.textContent = option.querySelector('.bundle-selector__option-label')?.textContent || '';
      label.removeAttribute('data-empty');
    }
    slot.classList.add('bundle-selector__slot--filled');

    this.#closeAllSlots();
    this.#update();
  }

  /** Reset every slot to its empty state (after a successful add). */
  #resetSlots() {
    for (const slot of this.#slotElements()) {
      const input = slot.querySelector('input[ref="slotValues[]"]');
      if (input instanceof HTMLInputElement) input.value = '';
      const media = slot.querySelector('[data-slot-media]');
      if (media instanceof HTMLElement) {
        media.innerHTML = '';
        media.setAttribute('hidden', '');
      }
      const label = slot.querySelector('[data-slot-label]');
      if (label instanceof HTMLElement) {
        label.textContent = label.dataset.placeholder || '';
        label.setAttribute('data-empty', '');
      }
      slot.classList.remove('bundle-selector__slot--filled');
      for (const option of slot.querySelectorAll('.bundle-selector__option')) {
        option.setAttribute('aria-selected', 'false');
      }
    }
  }

  get #i18n() {
    if (this.#i18nCache) return this.#i18nCache;
    try {
      const script = this.querySelector('script[data-bundle-i18n]');
      this.#i18nCache = script ? JSON.parse(script.textContent || '{}') : {};
    } catch {
      this.#i18nCache = {};
    }
    return this.#i18nCache;
  }

  /** @type {Record<string, string> | null} */
  #i18nCache = null;

  /** @param {string} key */
  #t(key) {
    return this.#i18n[key] || '';
  }

  /** Fixed (non-selectable) items: [{id, quantity}] */
  get #fixedItems() {
    try {
      const items = JSON.parse(this.dataset.fixedItems || '[]');
      return Array.isArray(items) ? items.filter((item) => item && item.id) : [];
    } catch {
      return [];
    }
  }

  get #selects() {
    return this.refs.slotValues || [];
  }

  get #allChosen() {
    return this.#selects.every((input) => input.value !== '');
  }

  #update() {
    if (!this.refs.addButton) return;
    const ready = this.#allChosen;
    this.refs.addButton.disabled = !ready;
    if (this.refs.addButtonText) {
      this.refs.addButtonText.textContent = ready ? this.#t('addToCart') : this.#t('chooseAll');
    }
  }

  /** @param {Event} event */
  async addToCart(event) {
    event.preventDefault();
    if (!this.#allChosen) return;

    const bundleId = `bb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const label = this.dataset.bundleLabel || 'Bundle';
    const propertyName = this.dataset.bundleProperty || 'Bundle';

    /** @type {Record<string, string>} */
    const properties = {
      _bundle_id: bundleId,
      [propertyName]: label,
    };
    if (this.dataset.bundleType) properties._bundle_type = this.dataset.bundleType;

    // Aggregate: same variant chosen in several slots → one line with
    // the summed quantity; fixed items are appended.
    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const select of this.#selects) {
      const id = Number(select.value);
      quantities.set(id, (quantities.get(id) || 0) + 1);
    }
    for (const fixed of this.#fixedItems) {
      const id = Number(fixed.id);
      quantities.set(id, (quantities.get(id) || 0) + (Number(fixed.quantity) || 1));
    }

    const items = [...quantities.entries()].map(([id, quantity]) => ({ id, quantity, properties }));
    const count = items.reduce((sum, item) => sum + item.quantity, 0);

    // Section ids of the rendered cart components, so the add response
    // carries fresh section HTML the cart listeners can morph in.
    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );

    this.refs.addButton.disabled = true;
    this.refs.addButtonText.textContent = this.#t('adding');
    this.#hideError();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        // /cart/add.js errors carry status + message/description.
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );

        const ajaxCart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'bundle-builder-component',
            sourceId: this.id.toString(),
            itemCount: count,
          },
        });

        this.#showError(result.description || result.message || this.#t('error'));
        this.#update();
        return;
      }

      const ajaxCart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'bundle-builder-component',
          sourceId: this.id.toString(),
          itemCount: count,
          sections: result.sections,
        },
      });

      // Success: clear the selection so a second bundle starts fresh
      // (a repeat add must get its own _bundle_id).
      this.#resetSlots();
      this.refs.addButtonText.textContent = this.#t('added');
      this.#resetTimeout = window.setTimeout(() => this.#update(), 2000);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.#t('error'));
      this.#update();
    }
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.refs.errorMessage) return;
    this.refs.errorMessage.textContent = message;
    this.refs.errorMessage.removeAttribute('hidden');
  }

  #hideError() {
    this.refs.errorMessage?.setAttribute('hidden', '');
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
